import { authedHeaders } from '../store/authStore';
import { recentClientErrors } from '../lib/clientErrorBuffer';

const INCIDENT_API: string = import.meta.env.VITE_INCIDENT_SERVICE_URL || 'http://localhost:8035';

export interface ReportAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface IssueReport {
  id: string;
  title: string;
  body: string;
  status: 'new' | 'triaged' | 'dismissed';
  created_at: string;
  triage_summary: string | null;
  triage_category: string | null;
  attachments: ReportAttachment[];
}

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({} as { detail?: string }));
    throw new Error((err as { detail?: string }).detail || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function createReport(title: string, body: string): Promise<{ id: string; triage_in_seconds: number }> {
  return j(await fetch(`${INCIDENT_API}/reports`, {
    method: 'POST',
    headers: authedHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      title,
      body,
      client_context: {
        url: window.location.href,
        user_agent: navigator.userAgent,
        errors: recentClientErrors(),
      },
    }),
  }));
}

export async function uploadScreenshot(reportId: string, file: File): Promise<ReportAttachment> {
  const form = new FormData();
  form.append('file', file, file.name || 'screenshot.png');
  // No Content-Type header: the browser sets the multipart boundary itself.
  return j(await fetch(`${INCIDENT_API}/reports/${reportId}/attachments`, {
    method: 'POST',
    headers: authedHeaders(),
    body: form,
  }));
}

export async function finalizeReport(reportId: string): Promise<{ status: string }> {
  return j(await fetch(`${INCIDENT_API}/reports/${reportId}/finalize`, {
    method: 'POST',
    headers: authedHeaders(),
  }));
}

export async function getReport(reportId: string): Promise<IssueReport> {
  return j(await fetch(`${INCIDENT_API}/reports/${reportId}`, { headers: authedHeaders() }));
}

export async function fetchAttachmentBlob(reportId: string, attachmentId: string): Promise<string> {
  const res = await fetch(`${INCIDENT_API}/reports/${reportId}/attachments/${attachmentId}`, {
    headers: authedHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return URL.createObjectURL(await res.blob());
}
